/**
 * Shared catalogue taxonomy.
 *
 * Derives a garment type and a dominant colour from a product's text (title +
 * brand + category), with no schema changes required. Used by:
 *   - Category page filtering (subcategory + colour facets)
 *   - the recommendation engine (similarity scoring)
 *   - "Complete the look" outfit matching
 *
 * Everything is keyword-based so it works on the demo catalogue today and on
 * real listings later without per-item tagging.
 */
import type { Product } from '@/components/product/ProductCard';

export interface CatalogItem extends Product {
  category?: string;
}

/** Minimal shape needed to derive garment/colour from free text. */
export type TextSource = {
  id?: string;
  title?: string;
  brand?: string;
  category?: string;
  subcategory?: string;
  styleTag?: string;
  priceGBP?: number;
};

export type Garment =
  | 'traditional' | 'dress' | 'skirt' | 'top' | 'shirt' | 'tshirt' | 'knitwear'
  | 'blazer' | 'suit' | 'jacket' | 'coat' | 'trousers' | 'jeans' | 'shorts'
  | 'shoes' | 'bag' | 'watch' | 'phone' | 'laptop' | 'accessory' | 'other';

/** Ordered most-specific first - the first matching pattern wins. */
const GARMENT_PATTERNS: [Garment, RegExp][] = [
  ['traditional', /ankara|adire|kente|aso[- ]?oke|agbada|dashiki|boubou|iro|buba|kaftan|caftan|abaya|thobe|saree|sari|lehenga|salwar|kameez|kurta|qipao|cheongsam|owambe|gele/],
  ['phone',    /iphone|smartphone|pixel|galaxy s\d|\bphone\b/],
  ['laptop',   /laptop|macbook|notebook|thinkpad/],
  ['watch',    /watch|submariner|datejust|tank solo|chronograph/],
  ['bag',      /\bbag\b|tote|holdall|clutch|backpack|crossbody|satchel|purse|cassette|margaux/],
  ['jeans',    /jean|denim/],
  ['shorts',   /shorts/],
  ['tshirt',   /t-?shirt|\btee\b/],
  ['shirt',    /shirt|blouse/],
  ['knitwear', /knit|jumper|sweater|cardigan|crewneck|pullover|sweatshirt|cashmere (?:jumper|crew|top)/],
  ['blazer',   /blazer/],
  ['suit',     /\bsuit\b/],
  ['coat',     /\bcoat\b|trench|overcoat|peacoat|\bmac\b|topper|cape/],
  ['jacket',   /jacket|bomber|parka|gilet|anorak|windbreaker/],
  ['skirt',    /skirt/],
  ['dress',    /dress|gown|frock/],
  ['trousers', /trouser|chino|\bpant|slacks/],
  ['shoes',    /trainer|sneaker|air ?max|\bshoe|\bboot|loafer|heel|sandal|pump|brogue|derby|oxford|slider|mule/],
  ['top',      /\btop\b|cami|vest|tank|bodysuit|corset/],
  ['accessory',/scarf|scarves|belt|sunglasses|\bhat\b|\bcap\b|wallet|jewell|necklace|bracelet|\bring\b|earring|glove|sunglass/],
];

const COLOUR_PATTERNS: [string, RegExp][] = [
  ['white',  /\bwhite|ivory|\boff-?white/],
  ['black',  /\bblack|jet|onyx|midnight/],
  ['navy',   /\bnavy|midnight blue/],
  ['blue',   /\bblue|indigo|cobalt|teal|denim|stonewash/],
  ['grey',   /\bgrey|\bgray|charcoal|slate|stone/],
  ['cream',  /\bcream|beige|ecru|oat|sand|nude/],
  ['camel',  /camel|\btan\b|caramel|walnut|brown|chocolate|tobacco/],
  ['red',    /\bred|crimson|scarlet|ruby|rouge|burgundy|wine|maroon/],
  ['pink',   /\bpink|rose|blush|fuchsia|magenta/],
  ['green',  /\bgreen|forest|sage|olive|khaki|emerald/],
  ['plum',   /plum|purple|lilac|violet|aubergine/],
  ['gold',   /\bgold|mustard|ochre|amber/],
  ['yellow', /yellow|lemon/],
  ['orange', /orange|rust|terracotta|clay/],
];

const text = (p: TextSource) =>
  `${p.title ?? ''} ${p.brand ?? ''} ${p.category ?? ''} ${p.subcategory ?? ''} ${p.styleTag ?? ''}`.toLowerCase();

export function garmentType(p: TextSource): Garment {
  const t = text(p);
  for (const [g, re] of GARMENT_PATTERNS) if (re.test(t)) return g;
  return 'other';
}

export function productColour(p: TextSource): string | undefined {
  const t = text(p);
  for (const [c, re] of COLOUR_PATTERNS) if (re.test(t)) return c;
  return undefined;
}

/* ───────────────────── Subcategory matching ───────────────────── */

// Map a token found in a sub-category label to the garment(s) it implies.
const SUB_TOKEN_GARMENTS: Record<string, Garment[]> = {
  dress: ['dress'], dresses: ['dress'], gown: ['dress'], occasionwear: ['dress'],
  skirt: ['skirt'], skirts: ['skirt'],
  top: ['top', 'shirt', 'tshirt'], tops: ['top', 'shirt', 'tshirt'], blouse: ['shirt'], blouses: ['shirt'],
  shirt: ['shirt'], shirts: ['shirt'], tshirt: ['tshirt'], 'tshirts': ['tshirt'], tee: ['tshirt'], tees: ['tshirt'],
  knitwear: ['knitwear'], knit: ['knitwear'], jumper: ['knitwear'], sweater: ['knitwear'], cardigan: ['knitwear'],
  jacket: ['jacket', 'coat', 'blazer'], jackets: ['jacket', 'coat', 'blazer'],
  coat: ['coat', 'jacket'], coats: ['coat', 'jacket'], outerwear: ['coat', 'jacket'],
  blazer: ['blazer'], blazers: ['blazer'], suit: ['suit', 'blazer'], suits: ['suit', 'blazer'], tailoring: ['suit', 'blazer'],
  trouser: ['trousers'], trousers: ['trousers'], chino: ['trousers'], chinos: ['trousers'], slacks: ['trousers'],
  jean: ['jeans'], jeans: ['jeans'], denim: ['jeans'],
  shorts: ['shorts'],
  activewear: ['top', 'trousers'], loungewear: ['top', 'trousers'],
  trainer: ['shoes'], trainers: ['shoes'], sneaker: ['shoes'], boot: ['shoes'], boots: ['shoes'],
  heel: ['shoes'], heels: ['shoes'], loafer: ['shoes'], loafers: ['shoes'], sandal: ['shoes'], sandals: ['shoes'],
  formal: ['shoes'], platforms: ['shoes'], sliders: ['shoes'],
  tote: ['bag'], totes: ['bag'], crossbody: ['bag'], clutch: ['bag'], clutches: ['bag'], backpack: ['bag'],
  backpacks: ['bag'], shoulder: ['bag'], mini: ['bag'], travel: ['bag'], belt: ['accessory'], belts: ['accessory'],
  jewellery: ['accessory'], scarf: ['accessory'], scarves: ['accessory'], sunglasses: ['accessory'],
  hat: ['accessory'], hats: ['accessory'], cap: ['accessory'], caps: ['accessory'], wallet: ['accessory'],
  wallets: ['accessory'], watch: ['watch'], watches: ['watch'],
  phone: ['phone'], phones: ['phone'], laptop: ['laptop'], laptops: ['laptop'],
  ankara: ['traditional'], kente: ['traditional'], adire: ['traditional'], 'tie': ['traditional'],
  saree: ['traditional'], sarees: ['traditional'], lehenga: ['traditional'], lehengas: ['traditional'],
  salwar: ['traditional'], kameez: ['traditional'], kaftan: ['traditional'], kaftans: ['traditional'],
  aso: ['traditional'], oke: ['traditional'], agbada: ['traditional'], abaya: ['traditional'],
  yoruba: ['traditional'], igbo: ['traditional'], embroidered: ['traditional'], dashiki: ['traditional'],
};

const STOP = new Set(['and', 'the', 'of', 'with', '&', 'in', 'set', 'sets', 'prints', 'print', 'new', 'vintage', 'premium', 'verified', 'open', 'to', 'offers', 'fabric', 'lengths', 'under', 'trending', 'accessories']);

/**
 * Does a product belong to a sub-category label such as "Shirts", "Coats & Jackets",
 * "Ankara & Kente"? Falls back to TRUE (no filtering) when the label carries no
 * recognisable garment keyword, so an unknown facet never blanks the grid.
 */
export function matchesSubcategory(p: TextSource, subLabel: string): boolean {
  const tokens = subLabel.toLowerCase().split(/[^a-z]+/).filter((w) => w && !STOP.has(w));
  if (tokens.length === 0) return true; // unrecognised facet - don't exclude anything

  // 1) Direct keyword hit (lets "Ankara" vs "Sarees" differ within Traditional).
  const t = text(p);
  if (tokens.some((tk) => tk.length >= 3 && t.includes(tk))) return true;

  // 2) Garment-type mapping (handles synonyms: "Knitwear" -> jumper/cardigan/etc).
  const wanted = new Set<Garment>();
  for (const tk of tokens) {
    const gs = SUB_TOKEN_GARMENTS[tk];
    if (gs) gs.forEach((g) => wanted.add(g));
  }
  if (wanted.size === 0) return true;
  return wanted.has(garmentType(p));
}

export function matchesColour(p: TextSource, colour: string): boolean {
  const c = productColour(p);
  if (!c) return false;
  if (c === colour) return true;
  // treat blue/navy and the warm neutrals as adjacent so the facet feels natural
  const adjacency: Record<string, string[]> = {
    blue: ['navy'], navy: ['blue'], cream: ['white', 'camel'], white: ['cream'],
    camel: ['cream'], grey: ['black'],
  };
  return (adjacency[colour] ?? []).includes(c);
}

/* ───────────────────── Outfit / complete-the-look ───────────────────── */

const COMPLEMENTS: Partial<Record<Garment, Garment[]>> = {
  shirt:    ['trousers', 'jeans', 'shoes'],
  tshirt:   ['jeans', 'shorts', 'shoes'],
  top:      ['jeans', 'skirt', 'trousers'],
  knitwear: ['trousers', 'jeans', 'coat', 'skirt'],
  blazer:   ['trousers', 'shirt', 'shoes'],
  suit:     ['shirt', 'shoes', 'watch'],
  jacket:   ['jeans', 'trousers', 'knitwear'],
  coat:     ['knitwear', 'trousers', 'shoes'],
  dress:    ['shoes', 'bag', 'jacket'],
  skirt:    ['top', 'shirt', 'knitwear', 'shoes'],
  jeans:    ['shirt', 'tshirt', 'knitwear', 'shoes'],
  trousers: ['shirt', 'knitwear', 'blazer', 'shoes'],
  shorts:   ['tshirt', 'top', 'shoes'],
  shoes:    ['jeans', 'trousers', 'dress'],
  bag:      ['dress', 'coat', 'shoes'],
  traditional: ['bag', 'shoes', 'accessory'],
};

// Preferred complement colours for a given base colour (classic pairings).
const COLOUR_PAIRS: Record<string, string[]> = {
  white:  ['black', 'navy', 'blue', 'camel', 'grey'],
  black:  ['white', 'cream', 'grey', 'camel', 'gold'],
  navy:   ['white', 'cream', 'camel', 'grey'],
  blue:   ['white', 'cream', 'grey', 'camel'],
  grey:   ['white', 'black', 'navy', 'pink'],
  cream:  ['navy', 'black', 'camel', 'green'],
  camel:  ['white', 'navy', 'cream', 'black'],
  red:    ['black', 'navy', 'cream', 'white'],
  pink:   ['grey', 'navy', 'white'],
  green:  ['cream', 'camel', 'white', 'navy'],
  plum:   ['grey', 'black', 'cream', 'gold'],
  gold:   ['black', 'navy', 'cream'],
  yellow: ['navy', 'grey', 'white'],
  orange: ['navy', 'cream', 'white'],
};

export interface OutfitPick<T> { item: T; reason: string }

/**
 * Suggest items that complete an outfit around `product`.
 * e.g. a white shirt -> black trousers + blue jeans, plus shoes.
 * Returns at most `limit` items, favouring one per complementary garment type
 * and classic colour pairings.
 */
export function getOutfitSuggestions<T extends CatalogItem>(
  product: TextSource,
  catalogue: T[],
  limit = 4,
): OutfitPick<T>[] {
  const baseType = garmentType(product);
  const wantTypes = COMPLEMENTS[baseType];
  if (!wantTypes || wantTypes.length === 0) return [];

  const baseColour = productColour(product);
  const prefColours = baseColour ? COLOUR_PAIRS[baseColour] ?? [] : [];

  const score = (c: T) => {
    let s = (c.saveCount ?? 0) / 100;
    const col = productColour(c);
    if (col && prefColours.includes(col)) s += 5;
    if (c.verified) s += 0.5;
    return s;
  };

  const pool = catalogue.filter((c) => c.id !== product.id && wantTypes.includes(garmentType(c)));
  const picks: OutfitPick<T>[] = [];
  const used = new Set<string>();

  // First pass: best candidate for each complementary type, in priority order.
  for (const wt of wantTypes) {
    const best = pool
      .filter((c) => garmentType(c) === wt && !used.has(c.id))
      .sort((a, b) => score(b) - score(a))[0];
    if (best) {
      used.add(best.id);
      const col = productColour(best);
      const reason = col && prefColours.includes(col)
        ? `${col.charAt(0).toUpperCase() + col.slice(1)} ${wt} · pairs with ${baseColour ?? 'this'}`
        : `Goes with your ${baseType}`;
      picks.push({ item: best, reason });
    }
    if (picks.length >= limit) break;
  }

  // Second pass: fill any remaining slots with the next-best matches.
  if (picks.length < limit) {
    for (const c of pool.sort((a, b) => score(b) - score(a))) {
      if (used.has(c.id)) continue;
      used.add(c.id);
      picks.push({ item: c, reason: `Completes the look` });
      if (picks.length >= limit) break;
    }
  }
  return picks.slice(0, limit);
}
